import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  withStyles,
  Typography,
  ListSubheader,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Checkbox,
  Chip,
  Box,
  IconButton,
} from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import DeleteIcon from '@material-ui/icons/Delete';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useSnackbar } from 'notistack';
import Autocomplete from '@material-ui/lab/Autocomplete';
import InventoryUserAutocomplete from '../common/InventoryUserAutocomplete';

const GET_ACTIVE_DEALS = gql`
  query GetActiveDealsForQuickNotes {
    active_deals_for_quick_notes {
      id
      name
      pipeline {
        id
        name
      }
      pipeline_stage {
        id
        name
      }
    }
  }
`;

const CREATE_QUICK_NOTE = gql`
  mutation CreateQuickNote(
    $inventory_user_id: ID!,
    $comment: String!,
    $time_spent: Int!,
    $activity_type_id: ID!,
    $category_ids: [ID!],
    $interaction_date: String!,
    $planned: Boolean!,
    $attachments: [File!],
    $pipeline_card_id: ID
  ) {
    createQuickNote(
      inventory_user_id: $inventory_user_id,
      comment: $comment,
      time_spent: $time_spent,
      activity_type_id: $activity_type_id,
      category_ids: $category_ids,
      interaction_date: $interaction_date,
      planned: $planned,
      attachments: $attachments,
      pipeline_card_id: $pipeline_card_id
    ) {
      id
    }
  }
`;

const CREATE_QUICK_NOTES_BULK = gql`
  mutation CreateQuickNotesBulk(
    $inventory_user_ids: [ID!]!
    $comment: String!
    $time_spent: Int!
    $activity_type_id: ID!
    $category_ids: [ID!]
    $interaction_date: String!
    $planned: Boolean!
    $attachments: [File!]
    $pipeline_card_id: ID
  ) {
    createQuickNotesBulk(
      inventory_user_ids: $inventory_user_ids
      comment: $comment
      time_spent: $time_spent
      activity_type_id: $activity_type_id
      category_ids: $category_ids
      interaction_date: $interaction_date
      planned: $planned
      attachments: $attachments
      pipeline_card_id: $pipeline_card_id
    ) {
      notes {
        id
        comment
        time_spent
        time_spent_formatted
        activity_type_id
        quick_note_category_id
        interaction_date
        planned
        created_at
      }
      count
      errors
    }
  }
`;

const ENQUEUE_QUICK_NOTES_BULK = gql`
  mutation EnqueueQuickNotesBulk(
    $inventory_user_ids: [ID!]!
    $comment: String!
    $time_spent: Int!
    $activity_type_id: ID!
    $category_ids: [ID!]
    $interaction_date: String!
    $planned: Boolean!
    $attachments: [File!]
    $pipeline_card_id: ID
  ) {
    enqueueQuickNotesBulk(
      inventory_user_ids: $inventory_user_ids
      comment: $comment
      time_spent: $time_spent
      activity_type_id: $activity_type_id
      category_ids: $category_ids
      interaction_date: $interaction_date
      planned: $planned
      attachments: $attachments
      pipeline_card_id: $pipeline_card_id
    ) {
      count
      message
      errors
    }
  }
`;

const GET_CATEGORIES_And_ACTIVITIES = gql`
  query GetCategoriesAndActivities {
    getQuickNoteCategories {
      id
      name
    }
    getActivityTypes {
      id
      name
    }
  }
`;

// Time spent options (15-minute increments from 15 minutes to 8 hours)
const TIME_SPENT_OPTIONS = [];
for (let i = 15; i <= 480; i += 15) {
  const hours = Math.floor(i / 60);
  const minutes = i % 60;
  let label;
  if (hours === 0) {
    label = `${minutes} minutes`;
  } else if (minutes === 0) {
    label = `${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    label = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
  }
  TIME_SPENT_OPTIONS.push({ value: i, label });
}

const styles = (theme) => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  fileUploadSection: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  attachmentChip: {
    margin: theme.spacing(0.5),
  },
  attachmentList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
});

const QuickNoteFormModal = ({ open, onClose, onNoteAdded, classes }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading: queryLoading } = useQuery(GET_CATEGORIES_And_ACTIVITIES);
  const { data: dealsData, loading: dealsLoading } = useQuery(GET_ACTIVE_DEALS);

  // Process categories to remove prefixes for display
  const processCategories = (categories) => {
    if (!categories) return { practiceExchange: [], successionPlanning: [], other: [] };
    
    const processed = categories.map(category => {
      const cleaned = category.name
        .replace('Practice Exchange   ', '')
        .replace('Succession Planning   ', '');
      
      // Capitalize 3-letter words (like rsp, erd, etc.)
      const displayName = cleaned.split(' ').map(word => 
        word.length === 3 ? word.toUpperCase() : word
      ).join(' ');
      
      return {
        ...category,
        displayName,
        section: category.name.startsWith('Practice Exchange   ') ? 'Practice Exchange' :
                category.name.startsWith('Succession Planning   ') ? 'Succession Planning' : 'Other'
      };
    });
    
    return {
      practiceExchange: processed.filter(c => c.section === 'Practice Exchange'),
      successionPlanning: processed.filter(c => c.section === 'Succession Planning'),
      other: processed.filter(c => c.section === 'Other')
    };
  };

  const { practiceExchange, successionPlanning, other } = processCategories(data?.getQuickNoteCategories);

  const [formData, setFormData] = useState({
    selectedUsers: [], // Always use multi-select mode
    categoryIds: [], // Changed from categoryId to categoryIds (array)
    activityTypeId: '',
    interactionDate: new Date(),
    timeSpent: '',
    comment: '',
    planned: false,
    selectedPipelineCard: null,
  });

  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});

  const [createQuickNote, { loading: mutationLoading }] = useMutation(CREATE_QUICK_NOTE, {
    onCompleted: () => {
      enqueueSnackbar('Quick Note created successfully!', { variant: 'success' });
      // Reload the page to show the new note
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      enqueueSnackbar(`Error creating note: ${error.message}`, { variant: 'error' });
    },
  });

  const [createQuickNotesBulk, { loading: bulkMutationLoading }] = useMutation(CREATE_QUICK_NOTES_BULK, {
    onCompleted: (data) => {
      const count = data.createQuickNotesBulk.count;
      enqueueSnackbar(`Successfully created ${count} Quick Note${count > 1 ? 's' : ''}!`, { variant: 'success' });
      // Reload the page to show the new notes
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      enqueueSnackbar(`Error creating bulk notes: ${error.message}`, { variant: 'error' });
    },
  });

  const [enqueueQuickNotesBulk, { loading: enqueueMutationLoading }] = useMutation(ENQUEUE_QUICK_NOTES_BULK, {
    onCompleted: (data) => {
      const count = data.enqueueQuickNotesBulk.count;
      enqueueSnackbar(`Successfully queued ${count} Quick Notes for creation. You'll receive a notification when complete.`, { variant: 'info' });
      // Close modal immediately for async operations, no need to reload since notes will be created later
      onClose();
      resetForm();
    },
    onError: (error) => {
      enqueueSnackbar(`Error queueing bulk notes: ${error.message}`, { variant: 'error' });
    },
  });

  const resetForm = () => {
    setFormData({
      selectedUsers: [], // Always use multi-select mode
      categoryIds: [], // Changed from categoryId to categoryIds
      activityTypeId: '',
      interactionDate: new Date(),
      timeSpent: '',
      comment: '',
      planned: false,
      selectedPipelineCard: null,
    });
    setAttachments([]);
    setErrors({});
  };
  
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    
    // Allowed file extensions
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.xls', '.xlsx', '.csv', '.eml', '.docx', '.one'];
    
    const validFiles = [];
    const invalidSizeFiles = [];
    const invalidTypeFiles = [];
    
    files.forEach(file => {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExtension) {
        invalidTypeFiles.push(file.name);
      } else if (file.size > maxSize) {
        invalidSizeFiles.push(`${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB)`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (invalidTypeFiles.length > 0) {
      enqueueSnackbar(
        `The following files have invalid file types: ${invalidTypeFiles.join(', ')}. Allowed types: PDF, PNG, JPG, XLS, XLSX, CSV, EML, DOCX, ONE`,
        { variant: 'error' }
      );
    }
    
    if (invalidSizeFiles.length > 0) {
      enqueueSnackbar(
        `The following files exceed the 10MB limit: ${invalidSizeFiles.join(', ')}`,
        { variant: 'warning' }
      );
    }
    
    if (validFiles.length > 0) {
      setAttachments(prev => [...prev, ...validFiles]);
      enqueueSnackbar(`${validFiles.length} file(s) added`, { variant: 'success' });
    }
    
    // Reset the input so the same file can be selected again if needed
    event.target.value = '';
  };
  
  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    // Ensure we always have an array
    setFormData({ ...formData, categoryIds: typeof value === 'string' ? [value] : value });
  };
  
  const handleDateChange = (date) => {
    setFormData({ ...formData, interactionDate: date });
  };
  
  const handleInventoryUserChange = (user) => {
    setFormData({ ...formData, selectedUsers: user || [] });
  };

  const getSubmitButtonText = () => {
    const userCount = formData.selectedUsers.length;
    if (userCount === 0) return 'Create Notes';
    if (userCount === 1) return 'Create Note';
    if (userCount <= 3) return `Create ${userCount} Notes (Instant)`;
    return `Create ${userCount} Notes (Background)`;
  };
  
  const validate = () => {
    const newErrors = {};
    
    // User validation (always multi-select mode)
    if (!formData.selectedUsers || formData.selectedUsers.length === 0) {
      newErrors.inventoryUser = 'At least 1 user is required';
    } else if (formData.selectedUsers.length > 10) {
      newErrors.inventoryUser = 'Maximum 10 users allowed for bulk creation';
    }
    
    if (!formData.categoryIds || formData.categoryIds.length === 0) {
      newErrors.categoryIds = 'At least one category is required';
    }
    if (!formData.activityTypeId) newErrors.activityTypeId = 'Activity Type is required';
    if (!formData.interactionDate) {
      newErrors.interactionDate = 'Interaction Date is required';
    } else {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (formData.interactionDate > today) {
        newErrors.interactionDate = 'Interaction date cannot be in the future';
      }
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      if (formData.interactionDate < oneYearAgo) {
        newErrors.interactionDate = 'Interaction date cannot be more than 1 year ago';
      }
    }

    if (!formData.timeSpent && formData.timeSpent !== 0) {
      newErrors.timeSpent = 'Time spent is required';
    } else if (formData.timeSpent <= 0) {
      newErrors.timeSpent = 'Time spent must be greater than 0';
    }

    if (!formData.comment) {
      newErrors.comment = 'Comment is required';
    } else if (formData.comment.length < 5) {
      newErrors.comment = 'Comment must be at least 5 characters long';
    } else if (formData.comment.length > 2000) {
      newErrors.comment = 'Comment cannot exceed 2000 characters';
    }

    if (typeof formData.planned !== 'boolean') {
      newErrors.planned = 'Please select planned or unplanned';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    const baseVariables = {
      comment: formData.comment,
      time_spent: parseInt(formData.timeSpent, 10),
      activity_type_id: formData.activityTypeId,
      category_ids: formData.categoryIds, // Use category_ids array instead of single quick_note_category_id
      interaction_date: formData.interactionDate.toISOString().split('T')[0],
      planned: formData.planned,
      pipeline_card_id: formData.selectedPipelineCard?.id,
    };

    // Only include attachments if there are files to upload
    if (attachments.length > 0) {
      baseVariables.attachments = attachments;
    }

    // Determine if bulk creation is needed based on number of selected users
    const userIds = formData.selectedUsers.map(user => user.id);
    const shouldUseBulk = userIds.length > 1;

    if (shouldUseBulk) {
      // Bulk creation for multiple users
      const bulkVariables = {
        ...baseVariables,
        inventory_user_ids: userIds,
      };

      const bulkMutationOptions = {
        variables: bulkVariables,
        context: attachments.length > 0 ? { hasUpload: true } : {},
      };

      if (userIds.length <= 3) {
        // Synchronous bulk creation for 2-3 users with attachments
        createQuickNotesBulk(bulkMutationOptions);
      } else {
        // Asynchronous bulk creation for 4-10 users with attachments
        enqueueQuickNotesBulk(bulkMutationOptions);
      }
    } else {
      // Single note creation with optional attachments
      createQuickNote({
        variables: {
          ...baseVariables,
          inventory_user_id: userIds[0],
        },
        context: attachments.length > 0 ? { hasUpload: true } : {},
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Quick Note</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {queryLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <InventoryUserAutocomplete
                  value={formData.selectedUsers}
                  onChange={handleInventoryUserChange}
                  isMulti={true}
                  maxSelections={10}
                  showSelectionManagement={true}
                  placeholder="Search and select multiple users..."
                  textFieldProps={{
                    label: 'Inventory Users',
                    required: true,
                    error: !!errors.inventoryUser,
                    helperText: errors.inventoryUser,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={dealsData?.active_deals_for_quick_notes || []}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    const name = option.name || 'Unnamed';
                    const pipelineName = option.pipeline?.name || 'Unknown Pipeline';
                    const stageName = option.pipeline_stage?.name || 'Unknown Stage';
                    return `${name} - ${pipelineName} (${stageName})`;
                  }}
                  value={formData.selectedPipelineCard}
                  onChange={(e, value) => setFormData({ ...formData, selectedPipelineCard: value })}
                  loading={dealsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Link to Case/Deal (optional)"
                      helperText="Associate this note with an active deal or case"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {dealsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.categoryIds}>
                  <InputLabel>Categories</InputLabel>
                  <Select
                    multiple
                    name="categoryIds"
                    value={formData.categoryIds}
                    onChange={handleCategoryChange}
                    renderValue={(selected) => {
                      // Get category names for selected IDs
                      const allCategories = [...practiceExchange, ...successionPlanning, ...other];
                      return selected
                        .map(id => {
                          const cat = allCategories.find(c => c.id === id);
                          return cat ? cat.displayName : id;
                        })
                        .join(', ');
                    }}
                  >
                    {practiceExchange.length > 0 && (
                      <ListSubheader disableSticky>Practice Exchange</ListSubheader>
                    )}
                    {practiceExchange.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <Checkbox checked={formData.categoryIds.indexOf(category.id) > -1} />
                        {category.displayName}
                      </MenuItem>
                    ))}
                    
                    {successionPlanning.length > 0 && (
                      <ListSubheader disableSticky>Succession Planning</ListSubheader>
                    )}
                    {successionPlanning.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <Checkbox checked={formData.categoryIds.indexOf(category.id) > -1} />
                        {category.displayName}
                      </MenuItem>
                    ))}
                    
                    {other.length > 0 && (
                      <ListSubheader disableSticky>Other</ListSubheader>
                    )}
                    {other.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <Checkbox checked={formData.categoryIds.indexOf(category.id) > -1} />
                        {category.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.categoryIds && <Typography color="error" variant="caption">{errors.categoryIds}</Typography>}
                  <FormHelperText>You can select multiple categories</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.activityTypeId}>
                  <InputLabel>Activity Type</InputLabel>
                  <Select
                    name="activityTypeId"
                    value={formData.activityTypeId}
                    onChange={handleInputChange}
                  >
                    {data?.getActivityTypes.map((act) => (
                      <MenuItem key={act.id} value={act.id}>{act.name}</MenuItem>
                    ))}
                  </Select>
                  {errors.activityTypeId && <Typography color="error" variant="caption">{errors.activityTypeId}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    fullWidth
                    label="Interaction Date"
                    format="yyyy-MM-dd"
                    value={formData.interactionDate}
                    onChange={handleDateChange}
                    required
                    error={!!errors.interactionDate}
                    helperText={errors.interactionDate}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.timeSpent}>
                  <InputLabel>Time Spent</InputLabel>
                  <Select
                    name="timeSpent"
                    value={formData.timeSpent}
                    onChange={handleInputChange}
                  >
                    {TIME_SPENT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.timeSpent && <Typography color="error" variant="caption">{errors.timeSpent}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name="comment"
                  label="Notes"
                  value={formData.comment}
                  onChange={handleInputChange}
                  required
                  error={!!errors.comment}
                  helperText={errors.comment || `${formData.comment.length}/2000`}
                />
              </Grid>

              {/* File Attachments Section - Available for all modes */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Attachments (Optional)
                </Typography>
                {formData.selectedUsers.length > 1 && (
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    Note: The same attachments will be added to all selected users' Quick Notes
                  </Typography>
                )}
                <input
                  type="file"
                  id="attachment-input"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.csv,.eml,.docx,.one"
                />
                <label htmlFor="attachment-input">
                  <Box className={classes.fileUploadSection}>
                    <AttachFileIcon color="primary" style={{ fontSize: 40, marginBottom: 8 }} />
                    <Typography variant="body2" color="textSecondary">
                      Click to select files or drag and drop
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Max 10MB per file · Allowed: PDF, PNG, JPG, XLS, XLSX, CSV, EML, DOCX, ONE
                    </Typography>
                  </Box>
                </label>
                
                {attachments.length > 0 && (
                  <Box className={classes.attachmentList}>
                    {attachments.map((file, index) => (
                      <Chip
                        key={index}
                        label={`${file.name} (${formatFileSize(file.size)})`}
                        onDelete={() => handleRemoveAttachment(index)}
                        deleteIcon={<DeleteIcon />}
                        color="primary"
                        variant="outlined"
                        className={classes.attachmentChip}
                      />
                    ))}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset" error={!!errors.planned}>
                  <Typography variant="subtitle1" gutterBottom>
                    Type
                  </Typography>
                  <RadioGroup
                    row
                    value={formData.planned}
                    onChange={(e) => setFormData({ ...formData, planned: e.target.value === 'true' })}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio color="primary" />}
                      label="Planned"
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio color="primary" />}
                      label="Unplanned"
                    />
                  </RadioGroup>
                  {errors.planned && (
                    <FormHelperText>{errors.planned}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained" 
          disabled={mutationLoading || bulkMutationLoading || enqueueMutationLoading}
        >
          {(mutationLoading || bulkMutationLoading || enqueueMutationLoading) ? 
            <CircularProgress size={24} /> : 
            getSubmitButtonText()
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

QuickNoteFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNoteAdded: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(QuickNoteFormModal); 